import { getDeviceId } from './deviceId';

interface PosthogShim {
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string) => void;
}

// `Window.posthog` is already declared in several other packages with a
// narrower shape (just `capture`). Redeclaring it here with the wider
// `PosthogShim` shape collides with those declarations (TS2717). Instead,
// keep the global type narrow and reach the fuller shim through a cast
// at the two assignment sites — at runtime everyone sees the same
// object, but TypeScript sees only the narrow contract.
declare global {
  interface Window {
    posthog?: { capture: (event: string, props?: Record<string, unknown>) => void };
  }
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

/** Safe localStorage helpers — boot must never crash on a private-browsing
 *  session where every storage call throws. */
function safeLsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeLsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
}
function safeMatchMedia(query: string): boolean {
  try { return window.matchMedia(query).matches; } catch { return false; }
}

// MVP: a console-logging shim plus a fire-and-forget POST to the PostHog
// capture endpoint when a key is configured. Avoids the 60KB posthog-js
// payload which would blow the creator-shell budget.
//
// Bulletproof: every storage / DOM / fetch call is wrapped so an analytics
// failure can never crash the synchronous boot sequence. Any thrown error
// degrades to a no-op shim — analytics is not load-bearing.
export function initAnalytics(): void {
  try {
    const deviceId = getDeviceId();
    const shim: PosthogShim = {
      capture(event, props) {
        try {
          const payload = { event, deviceId, ts: Date.now(), ...props };
          if (import.meta.env.DEV) console.debug('[ph]', payload);
          if (POSTHOG_KEY) {
            void fetch(`${POSTHOG_HOST}/capture/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              keepalive: true,
              body: JSON.stringify({
                api_key: POSTHOG_KEY,
                event,
                distinct_id: deviceId,
                properties: props ?? {},
                timestamp: new Date().toISOString()
              })
            }).catch(() => {});
          }
        } catch {
          /* an analytics emit must never throw out of an event handler */
        }
      },
      identify(id) {
        if (import.meta.env.DEV) console.debug('[ph identify]', id);
      }
    };
    (window as Window & { posthog?: PosthogShim }).posthog = shim;

    shim.capture('creator_session_started', {
      firstVisit: !safeLsGet('vs:creator:seen'),
      isStandalone:
        safeMatchMedia('(display-mode: standalone)') ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    });
    safeLsSet('vs:creator:seen', '1');
    shim.identify(deviceId);
  } catch (err) {
    // Catastrophic fallback — install a silent no-op shim so callers that
    // do `window.posthog?.capture(...)` keep working, then carry on.
    // eslint-disable-next-line no-console
    console.warn('[analytics] init failed, falling back to no-op shim', err);
    (window as Window & { posthog?: PosthogShim }).posthog = {
      capture: () => {},
      identify: () => {}
    };
  }
}
